import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { toast } from 'sonner';
import { ArrowLeft, Eye, EyeOff, Shield, Loader2, Fingerprint, Scan } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { useBiometricAuth } from '../hooks/useBiometricAuth';

// Google Icon SVG Component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

// Check if running as native app (Huawei/non-GMS compatible)
const isNativeApp = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [googleAvailable, setGoogleAvailable] = useState(!isNativeApp()); // Hide on native by default
  const [biometricLoading, setBiometricLoading] = useState(false);
  
  const { login, loginWith2FA } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Biometric auth hook
  const {
    isAvailable: biometricAvailable,
    biometryType,
    hasStoredCredentials,
    getStoredCredentials
  } = useBiometricAuth();
  
  // Check for auth error passed from OAuth callback
  const authError = location.state?.error;
  
  // Handle biometric login
  const handleBiometricLogin = useCallback(async () => {
    if (!biometricAvailable || !hasStoredCredentials) return;
    
    setBiometricLoading(true);
    try {
      const credentials = await getStoredCredentials();
      if (credentials) {
        const result = await login(credentials.email, credentials.password);
        
        if (result.success) {
          toast.success('Welcome back!');
          navigate('/dashboard');
        } else if (result.requires2FA) {
          setEmail(credentials.email);
          setPassword(credentials.password);
          setRequires2FA(true);
          toast.info('Please enter your 2FA code');
        } else {
          toast.error('Biometric login failed. Please try with your password.');
        }
      }
    } catch (error) {
      toast.error('Biometric authentication cancelled');
    } finally {
      setBiometricLoading(false);
    }
  }, [biometricAvailable, hasStoredCredentials, getStoredCredentials, login, navigate]);
  
  // Auto-trigger biometric on mount if available
  useEffect(() => {
    if (biometricAvailable && hasStoredCredentials && !requires2FA) {
      // Small delay to ensure UI is ready
      const timer = setTimeout(() => {
        handleBiometricLogin();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [biometricAvailable, hasStoredCredentials, requires2FA, handleBiometricLogin]);
  
  // Check if Google OAuth is available (hides on Huawei/non-GMS devices)
  useEffect(() => {
    // If running as native app, don't show Google OAuth (Huawei compatibility)
    if (isNativeApp()) {
      console.log('[Login] Native app detected - hiding Google OAuth for Huawei/GMS compatibility');
      setGoogleAvailable(false);
      return;
    }
    
    // On web, check if Google services are reachable with timeout
    const timeout = setTimeout(() => {
      // After 3 seconds, if we haven't confirmed Google is available, assume it's not
      // This helps in regions where Google is blocked
      if (!window.google?.accounts) {
        console.log('[Login] Google services not detected after timeout');
        // Keep Google button visible on web, but it will redirect to our OAuth handler
      }
    }, 3000);
    
    return () => clearTimeout(timeout);
  }, []);
  
  // Google OAuth handler - uses Emergent managed auth service
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin;
    const googleAuthUrl = `https://demobackend.emergentagent.com/auth/v1/env/oauth/google?redirect_url=${redirectUrl}`;
    window.location.href = googleAuthUrl;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      toast.success('Welcome back!');
      navigate('/dashboard');
    } else if (result.requires2FA) {
      setRequires2FA(true);
      toast.info('Please enter your 2FA code');
    } else {
      // Guard: ensure error is a string before passing to toast
      const errorMsg = typeof result.error === 'string' 
        ? result.error 
        : 'Login failed. Please try again.';
      toast.error(errorMsg);
    }
    
    setLoading(false);
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    if (otpCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    
    setLoading(true);
    const result = await loginWith2FA(email, password, otpCode);
    
    if (result.success) {
      toast.success('Welcome back!');
      navigate('/dashboard');
    } else {
      // Guard: ensure error is a string before passing to toast
      const errorMsg = typeof result.error === 'string' 
        ? result.error 
        : 'Verification failed. Please try again.';
      toast.error(errorMsg);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-background">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-1/4 w-1/2 h-1/2 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-teal-500/10 rounded-full blur-3xl" />
      </div>
      
      <div className="w-full max-w-md relative">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
          data-testid="back-to-home-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>
        
        <Card className="border-border/50 shadow-xl">
          <CardHeader className="space-y-1">
            <div className="flex items-center gap-3 mb-2">
              <img 
                src="/images/parachpay-logo.jpg" 
                alt="ParachPay Logo" 
                width="40"
                height="40"
                className="h-10 w-auto"
              />
              <span className="font-bold text-xl tracking-wide">PARACHPAY</span>
            </div>
            <CardTitle className="text-2xl font-heading">
              {requires2FA ? 'Two-Factor Authentication' : 'Welcome back'}
            </CardTitle>
            <CardDescription>
              {requires2FA 
                ? 'Enter the code from your authenticator app'
                : 'Sign in to your ParachPay account'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Biometric Login Button - Native apps only */}
            {!requires2FA && biometricAvailable && hasStoredCredentials && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-14 rounded-xl mb-4 gap-3 font-medium border-emerald-500/50 hover:bg-emerald-500/10"
                  onClick={handleBiometricLogin}
                  disabled={biometricLoading}
                  data-testid="biometric-login-btn"
                >
                  {biometricLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : biometryType === 'Face ID' ? (
                    <Scan className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <Fingerprint className="w-5 h-5 text-emerald-500" />
                  )}
                  <span>Sign in with {biometryType}</span>
                </Button>
                
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">Or use password</span>
                  </div>
                </div>
              </>
            )}
            
            {/* Google Sign In Button - Hidden on native apps (Huawei/non-GMS) */}
            {!requires2FA && googleAvailable && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-12 rounded-xl mb-4 gap-3 font-medium"
                  onClick={handleGoogleLogin}
                  data-testid="google-signin-btn"
                >
                  <GoogleIcon />
                  Continue with Google
                </Button>
                
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">Or continue with email</span>
                  </div>
                </div>
                
                {authError && (
                  <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                    {authError}
                  </div>
                )}
              </>
            )}
            
            {requires2FA ? (
              <form onSubmit={handle2FASubmit} className="space-y-6">
                <div className="flex flex-col items-center space-y-4">
                  <Shield className="w-16 h-16 text-emerald-500" />
                  <div className="flex justify-center">
                    <InputOTP 
                      maxLength={6} 
                      value={otpCode} 
                      onChange={setOtpCode}
                      data-testid="otp-input"
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full rounded-xl h-12"
                  disabled={loading || otpCode.length !== 6}
                  data-testid="verify-2fa-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Verify & Sign In'
                  )}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setRequires2FA(false);
                    setOtpCode('');
                  }}
                  data-testid="back-to-login-btn"
                >
                  Back to login
                </Button>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 rounded-xl"
                    data-testid="email-input"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link 
                      to="/forgot-password" 
                      className="text-sm text-emerald-600 dark:text-emerald-400 hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Forgot your password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="h-12 rounded-xl pr-10"
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full rounded-xl h-12"
                  disabled={loading}
                  data-testid="signin-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </form>
            )}
            
            {!requires2FA && (
              <div className="mt-6 text-center text-sm">
                <span className="text-muted-foreground">Don't have an account? </span>
                <Link to="/register" className="text-emerald-600 dark:text-emerald-400 hover:underline font-medium" data-testid="register-link">
                  Sign up
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
